/* eslint-disable @typescript-eslint/no-unused-vars */
import { memo, useState, useImperativeHandle, useCallback, forwardRef, useEffect } from 'react';
import { Button, Typography } from "@/design-system/Atoms";
import { BackArrowButton } from "@/design-system/Atoms/Button/BackButton";
import { useAdvancedStepper } from '@/hooks/useAdvancedStepper';
import type { StepConfig } from './types';
import CouponSection from '../CouponSection';
import { useCartService } from '@/rtkQuery/features/hooks/useCartService';
import { ROUTES } from '@/routes/routes.config';
import { useNavigate } from 'react-router-dom';


interface StepIndicatorProps {
    step: StepConfig;
    index: number;
    status: 'pending' | 'current' | 'completed' | 'skipped';
    onClick?: () => void;
    isClickable?: boolean;
}

const StepIndicator = memo<StepIndicatorProps>(({
    step,
    index,
    status,
    onClick,
    isClickable = false
}) => {
    const getStatusClasses = () => {
        switch (status) {
            case 'completed':
                return 'bg-green-500 text-white border-green-500';
            case 'current':
                return 'bg-blue-500 text-white border-blue-500 ring-2 ring-blue-200';
            case 'skipped':
                return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            default:
                return 'bg-gray-100 text-gray-500 border-gray-300';
        }
    };

    const getIcon = () => {
        switch (status) {
            case 'completed':
                return '✓';
            case 'skipped':
                return '⤴';
            default:
                return index + 1;
        }
    };

    return (
        <div className="flex items-center">
            <button
                className={`
          w-10 h-10 rounded-full border-2 flex items-center justify-center
          font-semibold text-sm transition-all duration-200
          ${getStatusClasses()}
          ${isClickable ? 'hover:scale-105 cursor-pointer' : 'cursor-default'}
        `}
                onClick={isClickable ? onClick : undefined}
                disabled={!isClickable}
                aria-label={`Step ${index + 1}: ${step.title}`}
            >
                {getIcon()}
            </button>
            <div className="ml-4 min-w-0 flex-1">
                <h4 className={`text-sm font-medium ${status === 'current' ? 'text-blue-600' :
                    status === 'completed' ? 'text-green-600' :
                        'text-gray-500'
                    }`}>
                    {step.title}
                    {step.isOptional && <span className="text-xs text-gray-400 ml-1">(Optional)</span>}
                </h4>
                {step.description && (
                    <p className="text-xs text-gray-500 mt-1">{step.description}</p>
                )}
            </div>
        </div>
    );
});

interface ProgressBarProps {
    progress: number;
    className?: string;
}

const ProgressBar = memo<ProgressBarProps>(({ progress, className = '' }) => (
    <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
        <div
            className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
            aria-label={`Progress: ${progress}%`}
        />
    </div>
));

interface AdvancedStepperProps<TData = any> {
    steps: StepConfig<TData>[];
    initialData: TData;
    onComplete?: (data: TData) => Promise<void> | void;
    onBack?: () => void;
    onStepChange?: (fromStep: string | null, toStep: string, data: TData) => void;
    onError?: (error: Error, step: string) => void;
    className?: string;
    showProgress?: boolean;
    allowStepNavigation?: boolean;
    persistKey?: string;
    autoSave?: boolean;
    showSteps?: boolean;
    profileData?: any;
    appliedCoupon?: any;
    onCouponApplied?: (couponData: any) => void;
}

export interface AdvancedStepperRef {
    goToStep: (stepId: string) => Promise<void>;
    setEditingSection: (sectionId: string) => void;
    getCurrentStep: () => string | null;
    getData: () => any;
    validateCurrentStep: () => Promise<boolean>;
}

const formatDate = (date: any): string => {
    if (!date) return 'Not provided';

    try {
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
            return `${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getDate().toString().padStart(2, '0')
                }/${dateObj.getFullYear()}`;
        }
    } catch (error) {
        console.warn('Unable to format date:', date);
    }

    return 'Invalid date';
};

export const AdvancedStepper = forwardRef<AdvancedStepperRef, AdvancedStepperProps>(function AdvancedStepper<TData = any>({
    steps,
    initialData,
    onComplete,
    onBack,
    onStepChange,
    onError,
    className = '',
    showProgress = false,
    allowStepNavigation = false,
    persistKey,
    autoSave = false,
    showSteps = false,
    profileData,
    appliedCoupon,
    onCouponApplied,

}: AdvancedStepperProps<TData>, ref: any) {
    const [editingSections, setEditingSections] = useState<Set<string>>(new Set());
    const [localAppliedCoupon, setLocalAppliedCoupon] = useState<any>(appliedCoupon || null);
    const navigate = useNavigate();
    const {
        cartData,
    } = useCartService() || {};

    const productId = cartData && cartData?.products?.[0]?.product_id;

    const stepper = useAdvancedStepper(steps, initialData, {
        onComplete,
        persistKey,
        autoSave,
        onStepChange: (from, to, data) => {

            if (from) {
                setEditingSections(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(from);
                    return newSet;
                });
            }

            onStepChange?.(from, to, data);
        },
        onError: (error, step) => {
            console.error(`Step error in ${step}:`, error);
            onError?.(error, step);
        }
    });

    const {
        currentStep,
        currentStepIndex,
        stepData,
        isLoading,
        errors,
        canGoPrevious,
        goNext,
        goPrevious,
        goToStep,
        skipStep,
        updateData,
        getStepStatus,
        getProgress,
        isFirstStep,
        isLastStep,
        completedSteps,
        validateStep
    } = stepper;

    const handleEditClick = useCallback((stepId: string) => {

        setEditingSections(prev => {
            const newSet = new Set(prev);
            newSet.add(stepId);
            return newSet;
        });

        if (currentStep?.id !== stepId) {
            goToStep(stepId);
        }
    }, [currentStep?.id, goToStep]);

    const handleBackClick = useCallback(() => {
        if (isLastStep && cartData?.products?.length === 0 && onBack) {
            navigate(ROUTES.PRODUCTS_LIST)
        } else if (canGoPrevious) {
            goPrevious();
        } else if (isFirstStep && onBack) {
            onBack();
        }
    }, [canGoPrevious, goPrevious, isFirstStep, isLastStep, onBack]);


    useImperativeHandle(ref, () => ({
        goToStep: async (stepId: string) => {
            await goToStep(stepId);
        },
        setEditingSection: (sectionId: string) => {
            handleEditClick(sectionId);
        },
        getCurrentStep: () => currentStep?.id || null,
        getData: () => stepData,
        validateCurrentStep: async () => {
            if (!currentStep) return false;
            try {
                const result = await validateStep(currentStep.id, stepData);
                return result.isValid;
            } catch (error) {
                console.error('Validation error:', error);
                return false;
            }
        }
    }), [currentStep, stepData, goToStep, handleEditClick, validateStep]);

    const CurrentStepComponent = currentStep?.component;
    const currentErrors = currentStep ? errors[currentStep.id] : undefined;

    const hasData = (obj: any) => {
        return obj && typeof obj === 'object' && Object.keys(obj).length > 0;
    };

    const typedStepData = stepData as any;
    const displayData = profileData || typedStepData;

    const hasSavedPaymentData = () => {
        return profileData?.paymentInfo?.cardHash &&
            profileData?.paymentInfo?.cardHash.trim() !== '';
    };

    const shouldShowPrefilledSections = () => {
        return currentStep?.id === 'review' &&
            hasSavedPaymentData() &&
            hasData(displayData.userInfo);
    };

    const shouldShowCouponSection = () => {

        const isReviewStep = currentStep?.id === 'review';
        const hasCompletedPayment = completedSteps.has('payment') || completedSteps.has('user-info');
        const notEditingUserInfo = !editingSections.has('user-info');
        const notEditingPayment = !editingSections.has('payment');

        // const shouldShow = isReviewStep && hasCompletedPayment && notEditingUserInfo && notEditingPayment;
        const shouldShow = isReviewStep;

        return shouldShow;
    };

    const handleSavedPaymentContinue = useCallback(() => {
        const paymentInfo = {
            paymentMethod: 'Credit Card',
            cardNumber: displayData.paymentInfo?.cardNumber,
            cardHolderName: displayData.paymentInfo?.cardHolderName,
            cardHash: displayData.paymentInfo?.cardHash,
            status: 'Success',
            timestamp: new Date().toISOString()
        };
        updateData({ paymentInfo } as any);
        goNext();
    }, [updateData, goNext, displayData.paymentInfo]);

    useEffect(() => {
        setLocalAppliedCoupon(appliedCoupon);
    }, [appliedCoupon]);

    return (
        <div className={`advanced-stepper ${className}`}>
            {showProgress && (
                <div className="mb-8">
                    <ProgressBar progress={getProgress() * 100} />
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                        <span>Progress</span>
                        <span>{Math.round(getProgress() * 100)}% Complete</span>
                    </div>
                </div>
            )}

            <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1">
                    <BackArrowButton
                        label="Back"
                        className="mb-5"
                        onClick={handleBackClick}
                        disabled={isLoading}
                    />

                    {((shouldShowPrefilledSections() && !editingSections.has('user-info')) ||
                        (completedSteps.has('user-info') &&
                            hasData(displayData.userInfo) &&
                            !editingSections.has('user-info') &&
                            currentStep?.id !== 'user-info')) && (
                            <div className="form-bg bg-[#f6f7f9] mb-6 p-8 rounded-[20px] animate-fade-in">
                                <h3 className="text-2xl mb-5 text-[#181a20]">Your Information</h3>

                                <div className="bg-white p-[1.25rem] mb-6 rounded-[8px] ">
                                    <div className="flex flex-wrap -mx-[0.75rem]">
                                        <div className="flex-[1_0_0%] px-[0.75rem] mb-4 md:mb-0">
                                            <p className="text-[1rem] font-bold mb-1 text-[#3B4151]">Full Name</p>
                                            <p className="mb-0 text-[1.25rem] text-[#181a20]">
                                                {displayData.userInfo?.FullName ||
                                                    (displayData.userInfo?.firstName && displayData.userInfo?.lastName
                                                        ? `${displayData.userInfo.firstName} ${displayData.userInfo.lastName}`
                                                        : 'Not provided'
                                                    )}
                                            </p>
                                        </div>

                                        <div className="flex-[1_0_0%] px-[0.75rem] mb-4 md:mb-0">
                                            <p className="text-[1rem] font-bold mb-1 text-[#3B4151]">Date of Birth</p>
                                            <p className="mb-0 text-[1.25rem] text-[#181a20]">
                                                {formatDate(displayData.userInfo?.DateOfBirth || displayData.userInfo?.dateOfBirth)}
                                            </p>
                                        </div>

                                        <div className="flex-[1_0_0%] px-[0.75rem] mb-4 md:mb-0">
                                            <p className="text-[1rem] font-bold mb-1 text-[#3B4151]">Address</p>
                                            <p className="mb-0 text-[1.25rem] text-[#181a20]">
                                                {displayData.userInfo?.Address || displayData.userInfo?.address
                                                    ? `${displayData.userInfo?.Address || displayData.userInfo?.address}, ${displayData.userInfo?.City || displayData.userInfo?.city || ''}, ${displayData.userInfo?.State || displayData.userInfo?.state || ''} ${displayData.userInfo?.ZipCode || displayData.userInfo?.zipCode || ''}`.trim()
                                                    : 'Not provided'
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    variant='secondary'
                                    onClick={() => handleEditClick('user-info')}
                                    className="transition-all duration-200 hover:shadow-md"
                                    disabled={isLoading}
                                    size='xl'
                                >
                                    Edit Your Information
                                </Button>
                            </div>
                        )}

                    {((shouldShowPrefilledSections() && !editingSections.has('payment')) ||
                        (completedSteps.has('payment') &&
                            hasData(displayData.paymentInfo?.cardHash) &&
                            !editingSections.has('payment') &&
                            currentStep?.id !== 'payment' &&
                            currentStepIndex >= 2) ||
                        (currentStep?.id === 'payment' &&
                            hasSavedPaymentData() &&
                            !editingSections.has('payment'))) && (
                            <div className="form-bg bg-[#f6f7f9] mb-6 p-8 rounded-[20px] animate-fade-in">
                                <h3 className="text-2xl mb-5 text-[#181a20]">Payment Information</h3>

                                <div className="bg-white p-5 mb-6 rounded-lg ">
                                    <h4 className="text-[1.25rem] mb-4 font-normal text-[#3B4151]">Credit Card</h4>
                                    <div className="flex flex-wrap gap-8">
                                        <div>
                                            <p className="text-[1rem] font-bold mb-1 text-[#3B4151]">Card Holder Name</p>
                                            <p className="mb-0 text-[1.25rem] text-[#181a20]">
                                                {displayData.paymentInfo?.cardHolderName || 'Not provided'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-[1rem] font-bold mb-1 text-[#3B4151]">Card Number</p>
                                            <p className="mb-0 text-[1.25rem] text-[#181a20]">
                                                {displayData.paymentInfo?.cardNumber
                                                    ? `Card Ends With ${displayData.paymentInfo.cardNumber}`
                                                    : 'Not provided'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-4 flex-wrap ">
                                    <Button
                                        variant='secondary'
                                        onClick={() => handleEditClick('payment')}
                                        className="transition-all duration-200 hover:shadow-md"
                                        disabled={isLoading}
                                        size='xl'
                                    >
                                        Edit Payment Information
                                    </Button>
                                    {currentStep?.id === 'payment' && (
                                        <Button
                                            variant="graySubmitButton"
                                            onClick={handleSavedPaymentContinue}
                                            className="transition-all duration-200 hover:shadow-md"
                                            disabled={isLoading}
                                            size='xl'
                                        >
                                            Continue to Review Order
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                    {shouldShowCouponSection() && (
                        <CouponSection
                            productId={productId}
                            onCouponApplied={(couponData) => {
                                if (couponData?.Result === "Success" && couponData?.Object) {
                                    setLocalAppliedCoupon(couponData.Object);
                                    onCouponApplied?.(couponData.Object);
                                }
                            }}
                        />
                    )}

                    {(currentStep?.id !== 'payment' ||
                        !hasSavedPaymentData() ||
                        editingSections.has('payment')) && (
                            <>
                                {/* <Typography
                                    variant="darkBackgroundHeading"
                                    as="h2"
                                    className="animate-fade-in-up !text-2xl !py-5 mb-6"
                                >
                                    {currentStepIndex + 1}. {currentStep?.title} ss
                                </Typography> */}

                                {currentErrors && currentErrors.length > 0 && (
                                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                                        <div className="flex items-start">
                                            <svg className="w-5 h-5 text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <div className="ml-3">
                                                <h4 className="text-sm font-medium text-red-800">Please fix the following issues:</h4>
                                                <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                                                    {currentErrors.map((error, index) => (
                                                        <li key={index}>{error}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="mb-10">
                                    {CurrentStepComponent && (
                                        <CurrentStepComponent
                                            data={stepData}
                                            onDataChange={updateData}
                                            onNext={goNext}
                                            onPrevious={goPrevious}
                                            onSkip={currentStep.isSkippable ? skipStep : undefined}
                                            isLoading={isLoading}
                                            errors={currentErrors}
                                            metadata={currentStep.metadata}
                                        />
                                    )}
                                </div>
                            </>
                        )}

                    <div className="space-y-4">
                        {steps.slice(currentStepIndex + 1).map((step, index) => (
                            <div
                                key={step.id}
                                className="text-[1.5rem] bg-[#f6f7f9] text-[#181a20] px-8 py-4 rounded-lg mb-5 transition-all duration-200 hover:"
                            >
                                <div className="flex items-center justify-between">
                                    <span>
                                        {currentStepIndex + index + 2}. {step.title}
                                        {step.isOptional && (
                                            <span className="text-sm text-gray-400 ml-2">(Optional)</span>
                                        )}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                        {step.shouldSkip?.(stepData) ? '' : ''}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {showSteps && (
                    <div className="w-full lg:w-80">
                        <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-6 ">
                            <h3 className="text-lg font-semibold mb-4">Checkout Progress</h3>
                            <div className="space-y-4">
                                {steps.map((step, index) => (
                                    <StepIndicator
                                        key={step.id}
                                        step={step}
                                        index={index}
                                        status={getStepStatus(step.id)}
                                        onClick={() => goToStep(step.id)}
                                        isClickable={allowStepNavigation &&
                                            (getStepStatus(step.id) === 'completed' ||
                                                getStepStatus(step.id) === 'current')}
                                    />
                                ))}
                            </div>

                            <div className="mt-6 pt-4 border-t">
                                <div className="text-sm text-gray-600 mb-2">
                                    Progress: {Math.round(getProgress() * 100)}%
                                </div>
                                <ProgressBar progress={getProgress() * 100} />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});